import { useMutation, UseMutationOptions, UseMutationResult, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData, IDeleteDataResponseAndPayload } from "../../../types/common";
import { IRecipientsListResponse } from "../../../types/api/recipients";
import { deleteRecipients, listRecipients, recipientsDetails, saveRecipients } from "./callbacks";

export const useRecipientsSave = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IRecipientsListResponse>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IRecipientsListResponse>, unknown, IRecipientsListResponse> = {}
): UseMutationResult<TResponse<IRecipientsListResponse>, unknown, IRecipientsListResponse> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IRecipientsListResponse>, unknown, IRecipientsListResponse>({
    mutationFn: async (data: IRecipientsListResponse) => {
      const response = await saveRecipients(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.RECIPIENTS_LIST, { ...qData, total: null }], (old: any) => {
        if (!old) return;
        const exists = old.data.find((v: any) => v._id === res.data._id);
       const newTotal = exists
            ? old.extra.total
            : old.extra.total + 1;


        return {
          ...old,
          data: exists  
            ? old.data.map((v: any) => (v._id === res.data._id ? res.data : v))
            : [res.data, ...old.data],
          extra: {
            ...old.extra,
            total: newTotal
          }
        };
      });

      queryClient.setQueryData(
      [API_ENDPOINTS.RECIPIENTS_DETAILS, res.data._id],
      {
        success: true,
        message: "",
        code: 200,
        data: res.data,
      }
    );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};

export const useRecipientsList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IRecipientsListResponse[]>, Error>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TResponseWithQData<IRecipientsListResponse[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IRecipientsListResponse[]>, Error>({
    queryKey: [API_ENDPOINTS.RECIPIENTS_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listRecipients(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map(x => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total }
    }),
    initialData: {
      data: [],
      success: true,
      message: '',
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};

export const useRecipientsDelete = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> = {}
): UseMutationResult<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload>({
    mutationFn: async (data: IDeleteDataResponseAndPayload) => {
      const response = await deleteRecipients(data);
      return response.data;
    },
    onSuccess: (res: TResponse<IDeleteDataResponseAndPayload>, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.RECIPIENTS_LIST, { ...qData, total: null }], (old: TResponse<IDeleteDataResponseAndPayload[]>) => {
        if (!old) return;

        const newTotal = old.extra?.total ? old.extra.total - 1 : 1;

        return {
          ...old,
          data: Array.isArray(res.data.ids) ? old.data.filter((v: any) => ~res.data.ids.includes(v._id)) : old.data.filter((v: any) => res.data.ids !== v._id),
          extra: {
            ...old.extra,
            total: newTotal
          }
        };
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};



export const useRecipientsDetails = (
  id: string,
  options?: Omit<
    UseQueryOptions<TResponse<IRecipientsListResponse>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IRecipientsListResponse>, Error> => {
  return useQuery<TResponse<IRecipientsListResponse>, Error>({
    queryKey: [API_ENDPOINTS.RECIPIENTS_DETAILS, id],
    queryFn: async () => {
      const res = await recipientsDetails(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};
